<script lang="ts">
    import SettingsBox from "../../elements/SettingsBox.svelte";
    import Title from "../../elements/settings/Title.svelte";
    import TitleIcon from "../../elements/settings/TitleIcon.svelte";
    import PrimaryBox from "../../elements/settings/PrimaryBox.svelte";
    import Pin from "../../icons/Pin.svelte";
    import Action from "../../elements/settings/Buttons/Action.svelte";
    import { pinnedDBisReady } from "../../../stores/globalState";
    import NestedBox from "../../elements/settings/NestedBox.svelte";
    import Booleans from "../../elements/settings/Buttons/Booleans.svelte";
    import { longPomodoro } from "../../../stores/storedSettings";
    import Hint from "../../elements/settings/Hint.svelte";
    import moment, { Moment } from "moment";
    import { notifications } from "../../../stores/notifications";
    import { locSto } from "../../../utils/utils";
    import { onMount } from "svelte";
    import { shortcuts, summoned } from "../../../stores/rooster";
    import time from "../../../stores/time";
    import { createIncomingEvent } from "../../clock/IncomingEventsMessages.svelte";
    import { altLabel } from "../../../costants";

    const defaultTitle = "启动计时器";
    let label = defaultTitle;
    let pomodoroIsRunning: "focus" | "relax" | false = false;
    let cycleEndsIn: number;
    let cycleEndsAt: Moment;
    let pomodoroTimer: NodeJS.Timeout;
    let timeLeft = "";
    let notificationTime: string[] = [];

    $: {
        if (pomodoroIsRunning && $time) {
            const t = `${cycleEndsAt.fromNow(true) === "an hour" ? "45 minutes" : cycleEndsAt.fromNow(true)}`;

            if (notificationTime.includes(t)) {
                createIncomingEvent({ icon: "icon-tomato-bw", text: `番茄钟：剩余 ${t}` });
                notificationTime.splice(notificationTime.indexOf(t), 1);
            }

            timeLeft = `[${t}]`;
        } else if (!pomodoroIsRunning) {
            timeLeft = "";
        }
    }

    function setPomodoroEndsAt(cycleEndsIn: number) {
        cycleEndsAt = moment().add(cycleEndsIn, "milliseconds");
        locSto("pomodoroEndsAt", cycleEndsAt.unix().toString());
    }

    function startRelaxSession() {
        pomodoroIsRunning = "relax";
        label = "休息时间到了 😊";
        locSto("pomodoroState", pomodoroIsRunning);

        cycleEndsIn = ($longPomodoro ? 15 : 5) * 60000;
        setPomodoroEndsAt(cycleEndsIn);

        pomodoroTimer = setTimeout(() => startFocusSession(), cycleEndsIn);
        notifications.create({
            title: label,
            icon: "icon-tomato-bw",
            limitDisplay: "notificationOnly",
            content: "休息一下",
            sound: true,
        });
    }

    function startFocusSession() {
        notificationTime = ["5 minutes", "2 minutes", "10 minutes"];
        label = "专注于你的工作！ 🤓";
        pomodoroIsRunning = "focus";
        locSto("pomodoroState", pomodoroIsRunning);
        const length = $longPomodoro ? 45 : 25;

        cycleEndsIn = length * 60000;
        setPomodoroEndsAt(cycleEndsIn);

        pomodoroTimer = setTimeout(() => startRelaxSession(), cycleEndsIn);
        notifications.create({
            title: label,
            icon: "icon-tomato-bw",
            limitDisplay: "notificationOnly",
            content: "保持专注 " + length + " 分钟",
            sound: true,
        });
    }

    function toggleTimer() {
        clearTimeout(pomodoroTimer);
        pomodoroIsRunning = pomodoroIsRunning ? false : "focus";
        locSto("pomodoroState", pomodoroIsRunning ? pomodoroIsRunning : null);

        if (pomodoroIsRunning) startFocusSession();
        else label = defaultTitle;
    }

    onMount(() => {
        locSto("pomodoroState", null);
        shortcuts.set("pomodoro", {
            color: "black",
            background: "red",
            arguments: {
                start: {
                    quickLaunch: "f",
                    quickLaunchTriggerActions: true,
                    description: "启动番茄钟",
                    async callback(p, item, action) {
                        if (action === 3) {
                            toggleTimer();
                            summoned.set(false);
                            return true;
                        }

                        if (!pomodoroIsRunning) {
                            p = p.replace(/\s/g, "");
                            if (p === "+") longPomodoro.set(true);
                            else if (p === "-") longPomodoro.set(false);
                            toggleTimer();
                        } else return false;
                        return true;
                    },
                },
                stop: {
                    description: "停止番茄钟（如果正在运行）",
                    async callback() {
                        if (pomodoroIsRunning) toggleTimer();
                        else return false;
                        return true;
                    },
                },
            },
            async examples(a, p) {
                return {
                    namespace: "示例",
                    group: [
                        { argument: "start", example: "", tip: "使用默认设置启动番茄钟" },
                        { argument: "start", example: "[ + 或 - ]", tip: '"+" 启动长时段，"-" 启动短时段' },
                        { example: "", argument: "stop", tip: "停止计时器（如果正在运行）" },
                    ],
                };
            },
        });
    });
</script>

<SettingsBox id="pomodoro">
    <Title title="番茄钟">
        <TitleIcon>
            <i id="pomodoro-icon" class="settings-icon"
                ><span class="icon-tomato-22"><span class="path1" /><span class="path2" /><span class="path3" /><span class="path4" /><span class="path5" /></span></i
            >
        </TitleIcon>
    </Title>
    <div>
        <PrimaryBox
            label={{ text: label, bgClass: pomodoroIsRunning === "focus" ? "bg-red-900" : pomodoroIsRunning === "relax" ? "bg-green-900" : null }}
            description={{ text: "" }}
            available={true}
        >
            <Action label={pomodoroIsRunning ? "停止" : "开始"} on:click={toggleTimer} />
        </PrimaryBox>
    </div>
    <NestedBox bordered label="更长的番茄钟" available={pomodoroIsRunning === false}>
        <Booleans state={$longPomodoro} label={"更长的番茄钟"} on:change={(e) => longPomodoro.set(e.detail)} />
    </NestedBox>
    <Hint
        text={"番茄钟高效地将你的工作流程分为25分钟的工作时段和5分钟的休息时间。如果你真的是一个努力工作的人，也可以设置更长的45/15分钟时段"}
        iconClass={"lnr lnr-question-circle"}
    />
</SettingsBox>
